import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { ModeToggle } from "@/components/mode-toggle";
import { useNavigate, useParams } from "react-router-dom";
import { MdOutlineMarkEmailUnread, MdError } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import Logo from "@/components/logo";
import { useAuth } from "../context/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { motion, AnimatePresence } from "framer-motion";

const VerifyEmail = () => {
    const { tokenId } = useParams();
    const navigate = useNavigate();
    const [verificationCode, setVerificationCode] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState(null);
    const [alreadyAuthenticated, setAlreadyAuthenticated] = useState(false);
    const hasVerified = useRef(false);
    const { verifyCode, isAuthLoading } = useAuth();

    // Poll localStorage every 5 seconds to determine if the user is already authenticated
    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem('accesstoken');
            const user = localStorage.getItem('user');
            setAlreadyAuthenticated(!!(token && user));
        };
        checkAuth();
        const intervalId = setInterval(checkAuth, 5000);
        return () => clearInterval(intervalId);
    }, []);

    const handleVerification = useCallback(async () => {
        if (hasVerified.current || isAuthLoading) return;
        hasVerified.current = true;
        setIsVerifying(true);
        setError(null);
        
        try {
            const result = await verifyCode(tokenId, verificationCode);
            
            if (result.success) {
                setTimeout(() => {
                    navigate(result.needsOnboarding ? '/onboarding' : '/today');
                }, 500);
            } else {
                setError(result.error);
                hasVerified.current = false;
                setIsVerifying(false);
                setVerificationCode('');
            }
        } catch (error) {
            setError('An error occurred during verification. Please try again later.');
            hasVerified.current = false;
            setIsVerifying(false);
            setVerificationCode('');
        }
    }, [tokenId, verificationCode, navigate, verifyCode, isAuthLoading]);

    useEffect(() => {
        if (verificationCode.length === 6 && !hasVerified.current) {
            handleVerification();
        }
    }, [verificationCode, handleVerification, isAuthLoading]);

    const handleGoBack = () => navigate('/login');

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
            <div className="absolute top-8 left-8">
                <Logo onClick={() => { navigate('/') }} />
            </div>
            <div className="absolute top-8 right-8 flex items-center space-x-4">
                <ModeToggle />
            </div>
            {alreadyAuthenticated ? (
                // New centered card to indicate that the user is already logged in on another tab.
                <div className="border w-full md:max-w-md bg-card text-card-foreground rounded-lg shadow-lg p-4 md:p-8 space-y-4">
                    <h1 className="text-2xl md:text-4xl text-center">You're logged in!</h1>
                    <p className="text-sm md:text-base text-muted-foreground text-center">
                        It looks like you're authenticated in another tab, which means you can safely close this one.
                    </p>
                </div>
            ) : (
                // Existing verification code entry module
                <div className="border w-full md:max-w-md bg-card text-card-foreground rounded-lg shadow-lg p-4 md:p-8 space-y-4">
                    {isVerifying ? (
                        <div className="flex items-center justify-center space-x-2">
                            <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
                            <h1 className="text-lg md:text-xl font-semibold animate-pulse">Authenticating your session</h1>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-start mb-2 md:mb-4">
                                <MdOutlineMarkEmailUnread className="text-4xl md:text-5xl text-muted-foreground" />
                            </div>
                            <h1 className="text-2xl md:text-4xl">Enter verification code from your email</h1>
                            <p className="text-sm md:text-base text-muted-foreground">
                                Please enter the code we emailed you.
                            </p>
                            <form>
                                <div className="relative py-4 flex justify-start">
                                    <InputOTP
                                        maxLength={6}
                                        value={verificationCode}
                                        onChange={setVerificationCode}
                                        autoFocus
                                    >
                                        <InputOTPGroup>
                                            <InputOTPSlot index={0} />
                                            <InputOTPSlot index={1} />
                                            <InputOTPSlot index={2} />
                                        </InputOTPGroup>
                                        <InputOTPSeparator />
                                        <InputOTPGroup>
                                            <InputOTPSlot index={3} />
                                            <InputOTPSlot index={4} />
                                            <InputOTPSlot index={5} />
                                        </InputOTPGroup>
                                    </InputOTP>
                                </div>
                                <AnimatePresence>
                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <Alert variant="destructive">
                                                <AlertDescription>{error}</AlertDescription>
                                            </Alert>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </form>
                            <div className="space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    Didn't receive the email? Check your spam folder or
                                </p>
                                <Button variant="link" onClick={handleGoBack} className="p-0">
                                    Go back and try again
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default VerifyEmail;
